import React from "react";
import { graphql, useFragment } from "react-relay";

import { faPaperPlane } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import { isTeams } from "~/lib/utils";
import ShareModal from "~/stream/ShareModal";
import { useModals } from "~/ui/modals";

import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "./MenuItemTooltip";

import type { ShareSeries_note$key } from "~/__generated__/ShareSeries_note.graphql";
import type { ShareSeries_viewer$key } from "~/__generated__/ShareSeries_viewer.graphql";

const viewerFragment = graphql`
	fragment ShareSeries_viewer on Viewer {
		user {
			id
		}
		account {
			billingAccount {
				hasAccessToSharing: hasAccessToFeature(feature: sharing)
			}
		}
	}
`;

const noteFragment = graphql`
	fragment ShareSeries_note on Note {
		stream {
			id
			displayName
			owner {
				id
			}
			notestreamusers {
				id
				accessType
				user {
					id
					email
					image
					fullName
					lastName
					firstName
					isActive
					isConfigured
					isGuest
					hasReceivedInvites
					isTopUser
					isExternal
					account {
						id
						name
					}
				}
			}
		}
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
	}
`;

export default function ShareSeriesMenuItem({
	note: noteKey,
	viewer: viewerKey,
}: {
	note: ShareSeries_note$key;
	viewer: ShareSeries_viewer$key;
}) {
	const modals = useModals();
	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);
	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	const currentUserMember = note.stream.notestreamusers.find(nsu => nsu.user.id === viewer.user.id);
	const menuItemDisabled = !currentUserMember || !["full", "shared"].includes(currentUserMember.accessType);

	const onOpenShareModal = React.useCallback(() => {
		const stream = note.stream;
		if (!stream) return;

		modals.open(({ close }) => (
			<ShareModal
				streamId={stream.id}
				ownerId={stream.owner?.id ?? ""}
				onClose={close}
				sharedNoteStreamUsers={stream.notestreamusers}
				showSharingPaywall={!(viewer.account.billingAccount.hasAccessToSharing || isTeams)}
				deferredExternals={[]}
			/>
		));
	}, [modals, note, viewer.account.billingAccount.hasAccessToSharing]);

	return (
		<MenuItemToBeDisabledForExternalWorkspaces
			onSelect={onOpenShareModal}
			icon={faPaperPlane}
			disabled={menuItemDisabled}
			{...tooltipPropsForExternalWorkspaces}
		>
			<MenuItemTooltip content="Full series access required" disabled={!menuItemDisabled}>
				<span>Share series…</span>
			</MenuItemTooltip>
		</MenuItemToBeDisabledForExternalWorkspaces>
	);
}
